import { Matrix4, Plane, Ray, Vector3, type CameraView } from '@/math'
import type { Vec3 } from '../voxelTypes'
import { closestLineParamFromPointer } from './scalar'

function toVector3(v: Vec3 | Vector3): Vector3 {
  return v instanceof Vector3 ? v : new Vector3(v.x, v.y, v.z)
}

// Intersect the pointer ray with a plane defined by origin and normal.
// Returns the world-space intersection point, or null if parallel.
export function projectPointerToPlane(
  ev: PointerEvent,
  camera: CameraView,
  canvas: HTMLCanvasElement,
  planeOrigin: Vec3 | Vector3,
  planeNormal: Vec3 | Vector3
): Vector3 | null {
  const rect = canvas.getBoundingClientRect()
  const ndcX = ((ev.clientX - rect.left) / rect.width) * 2 - 1
  const ndcY = -(((ev.clientY - rect.top) / rect.height) * 2 - 1)

  const proj = (camera as any).projectionMatrix
  const invProjView = new Matrix4().multiplyMatrices(
    (camera as any).matrixWorld,
    new Matrix4().copy(proj).invert()
  )
  const pNear = new Vector3(ndcX, ndcY, -1).applyMatrix4(invProjView)
  const pFar = new Vector3(ndcX, ndcY, 1).applyMatrix4(invProjView)
  const ray = new Ray(pNear, pFar.sub(pNear).normalize())

  const plane = new Plane().setFromNormalAndCoplanarPoint(
    toVector3(planeNormal).clone().normalize(),
    toVector3(planeOrigin)
  )

  const intersection = new Vector3()
  return ray.intersectPlane(plane, intersection) ? intersection : null
}

// Alias for naming symmetry with projectPointerToPlane (C22).
// Computes the scalar s for the closest point on a line (origin + s*dir) to the pointer ray.
export function projectPointerToLineScalar(
  ev: PointerEvent,
  camera: CameraView,
  rect: any,
  origin: Vec3 | Vector3,
  dir: Vec3 | Vector3
): number {
  return closestLineParamFromPointer(ev, camera, rect, origin, dir)
}

// C40: Construct a Ray from a pointer event
export function pointerRay(
  ev: PointerEvent,
  camera: CameraView,
  canvas: HTMLCanvasElement
): Ray {
  const rect = canvas.getBoundingClientRect()
  const ndcX = ((ev.clientX - rect.left) / rect.width) * 2 - 1
  const ndcY = -(((ev.clientY - rect.top) / rect.height) * 2 - 1)
  const proj = (camera as any).projectionMatrix
  const invProjView = new Matrix4().multiplyMatrices(
    (camera as any).matrixWorld,
    new Matrix4().copy(proj).invert()
  )
  const pNear = new Vector3(ndcX, ndcY, -1).applyMatrix4(invProjView)
  const pFar = new Vector3(ndcX, ndcY, 1).applyMatrix4(invProjView)
  return new Ray(pNear, pFar.sub(pNear).normalize())
}
